import { useState, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useCercaUsuaris } from '@/api/activacions';
import { esConflicteVersio } from '@/api/etag';
import { useMissatgeError } from '@/api/errors';
import {
  desaTorn,
  nomUsuari,
  useArees,
  useCalendari,
  useDepartaments,
  useEliminaTorn,
  useInvalidaTorns,
  useNoms,
  useRotacions,
  useTornsAmbEtag,
  useUsuaris,
  type TornDto,
} from '@/api/planificacio';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Avatar } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import { useMe } from '@/permisos/MeContext';
import {
  aplicaAssignacio,
  cobreixDia,
  dataCurta,
  diaClau,
  diaSetmana,
  dilluns,
  nomAmbit,
  nouTorn,
  participants,
  rangDies,
  rotacioActiva,
  sumaDies,
  tornsModificats,
} from './calendari';
import { W12ModalTorn, type Persona } from './W12ModalTorn';
import './planificacio.css';

const DIES = 14;

function PersonaArrossegable({
  persona,
  detall,
  seleccionada,
  onSelecciona,
}: {
  persona: Persona;
  detall: string;
  seleccionada: boolean;
  onSelecciona: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `persona-${persona.id}`,
    data: { personaId: persona.id },
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      className="list-row"
      data-persona={persona.id}
      onClick={onSelecciona}
      style={{
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 5 : undefined,
      }}
      {...attributes}
      {...listeners}
      aria-pressed={seleccionada}
    >
      <Avatar nom={persona.nom} />
      <span className="list-row__text">
        <span className="list-row__label" style={{ display: 'block' }}>
          {persona.nom}
        </span>
        <span className="list-row__sub" style={{ display: 'block' }}>
          {detall}
        </span>
      </span>
      {seleccionada ? <Icona nom="checkmark-circle" className="list-row__chev c-blue" /> : null}
    </button>
  );
}

/** Casella d'un dia (columna) on es pot deixar anar una persona. */
function Cel({
  dia,
  fila,
  cobertura,
  children,
}: {
  dia: Date;
  fila: string;
  cobertura?: boolean;
  children?: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `dia-${diaClau(dia)}-${fila}`,
    data: { dia },
  });
  return (
    <div
      ref={setNodeRef}
      className={`sg-cel${cobertura ? ' sg-cel--cob' : ''}${isOver ? ' is-over' : ''}`}
    >
      {children}
    </div>
  );
}

export function W11EditorCalendari() {
  const { t } = useTranslation('planificacio');
  const { t: te } = useTranslation('errors');
  const missatgeError = useMissatgeError();
  const f = useFormat();
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const { pot } = useMe();
  const [desde, setDesde] = useState(() => dilluns());
  const fins = sumaDies(desde, DIES);
  const desdeIso = desde.toISOString();
  const finsIso = fins.toISOString();
  const calendari = useCalendari(id);
  const torns = useTornsAmbEtag(id, desdeIso, finsIso);
  const rotacions = useRotacions(id);
  const departaments = useDepartaments();
  const arees = useArees();
  const usuaris = useUsuaris();
  const noms = useNoms();
  const invalida = useInvalidaTorns();
  const eliminaTorn = useEliminaTorn();
  const [esborrany, setEsborrany] = useState<TornDto[] | null>(null);
  const [etagsDesats, setEtagsDesats] = useState<Record<number, string | null>>({});
  const [desant, setDesant] = useState(false);
  const [errorDesa, setErrorDesa] = useState<unknown>(null);
  const [extres, setExtres] = useState<Persona[]>([]);
  const [seleccionada, setSeleccionada] = useState<number | null>(null);
  const [editant, setEditant] = useState<{ torn: TornDto; index: number } | null>(null);
  const [cerca, setCerca] = useState('');
  const cercaUsuaris = useCercaUsuaris(cerca);
  const potEditar = pot('cai.torns', 'MODIFICAR') || pot('cai.torns', 'ALTA');

  const originals = torns.data?.torns ?? [];
  const etagDe = (tornId: number) =>
    tornId in etagsDesats ? etagsDesats[tornId] : torns.data?.etags[tornId];

  const canviaPeriode = (d: Date) => {
    setEsborrany(null);
    setErrorDesa(null);
    setDesde(d);
  };

  const actuals = esborrany ?? originals;
  const canvis = tornsModificats(originals, actuals);
  const dies = rangDies(desde, DIES);
  const avuiClau = diaClau(new Date());
  const area = arees.data?.find((a) => a.id === calendari.data?.areaId);
  const crisi = area?.tipus === 'CRISI';
  const rotacio = rotacioActiva(rotacions.data);
  const horaCanvi = rotacio?.horaCanvi?.slice(0, 5) ?? '08:00';
  const ambit = calendari.data
    ? nomAmbit(calendari.data, departaments.data ?? [], arees.data ?? [])
    : '';

  const nomPersona = (pid: number | null | undefined) =>
    extres.find((p) => p.id === pid)?.nom ?? noms(pid);
  const files = participants(
    actuals,
    extres.map((p) => p.id),
  );
  const disponibles: Persona[] = [];
  const afegeixDisponible = (p: Persona) => {
    if (!disponibles.some((x) => x.id === p.id)) disponibles.push(p);
  };
  files.forEach((pid) => afegeixDisponible({ id: pid, nom: nomPersona(pid) }));
  extres.forEach(afegeixDisponible);
  (usuaris.data ?? []).forEach((u) => afegeixDisponible({ id: u.id, nom: nomUsuari(u) }));
  const tornsDe = (pid: number) =>
    actuals.filter((x) => x.titularId === pid || x.substitutId === pid).length;

  const assigna = (dia: Date, personaId: number) => {
    if (!potEditar) return;
    setEsborrany(aplicaAssignacio(actuals, id, dia, personaId, horaCanvi));
  };

  const obreDia = (dia: Date) => {
    const index = actuals.findIndex((x) => cobreixDia(x, dia));
    if (index >= 0) {
      setEditant({ torn: actuals[index] as TornDto, index });
    } else if (seleccionada != null) {
      assigna(dia, seleccionada);
    } else if (potEditar) {
      setEditant({ torn: nouTorn(id, dia, 0, horaCanvi), index: -1 });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const enDeixar = (e: DragEndEvent) => {
    const personaId = e.active.data.current?.personaId as number | undefined;
    const dia = e.over?.data.current?.dia as Date | undefined;
    if (personaId != null && dia) assigna(dia, personaId);
  };

  /**
   * Desa els canvis un a un. Cada torn creat rep l'id del servidor dins l'esborrany abans de
   * passar al següent: si un desament falla, el reintent fa PUT dels ja creats (no els torna a
   * crear) i només reenvia els pendents.
   */
  const desa = async () => {
    setDesant(true);
    setErrorDesa(null);
    let treball = [...actuals];
    try {
      for (const torn of canvis) {
        const { data, etag } = await desaTorn(torn, {
          etag: torn.id != null ? etagDe(torn.id) : undefined,
        });
        const desat: TornDto = { ...torn, ...(data ?? {}), id: data?.id ?? torn.id };
        treball = treball.map((x) => (x === torn ? desat : x));
        setEsborrany(treball);
        if (desat.id != null) {
          const tornId = desat.id;
          setEtagsDesats((e) => ({ ...e, [tornId]: etag }));
        }
      }
      setEsborrany(null);
      setEtagsDesats({});
      invalida();
    } catch (e) {
      setErrorDesa(e);
    } finally {
      setDesant(false);
    }
  };

  const recarrega = () => {
    setEsborrany(null);
    setEtagsDesats({});
    setErrorDesa(null);
    invalida();
  };

  if (calendari.isError) {
    return (
      <section className="pantalla" data-screen="W-11">
        <ErrorCarrega onReintenta={() => void calendari.refetch()} />
      </section>
    );
  }

  const missatgeConflicte = te('error.etag.no.coincideix', { keySeparator: false });

  const blocTorn = (_torn: TornDto, rol: 'T' | 'S') =>
    rol === 'S' ? 'sg-block--subst' : crisi ? 'sg-block--crisi' : 'sg-block--titular';

  return (
    <section className="pantalla" data-screen="W-11">
      <Toolbar
        titol={calendari.data?.nom ?? t('titols.editor')}
        enrere="/planificacio/calendaris"
        badges={
          <span className="badge badge--gray">
            {rotacio ? t(`tipusRotacio.${rotacio.periodicitat}`) : t('tipusRotacio.MANUAL')}
          </span>
        }
      >
        <div className="editor__navegacio">
          <div className="segmented" role="group" aria-label={t('editor.periodeEtiqueta')}>
            <button
              type="button"
              aria-label={t('editor.anterior')}
              title={t('editor.anterior')}
              onClick={() => canviaPeriode(sumaDies(desde, -7))}
            >
              <Icona nom="chevron-left" />
            </button>
            <button
              type="button"
              aria-pressed="true"
              title={t('editor.avui')}
              onClick={() => canviaPeriode(dilluns())}
            >
              {t('editor.periode', {
                desde: dataCurta(desde, f.idioma),
                fins: dataCurta(sumaDies(fins, -1), f.idioma),
              })}
            </button>
            <button
              type="button"
              aria-label={t('editor.seguent')}
              title={t('editor.seguent')}
              onClick={() => canviaPeriode(sumaDies(desde, 7))}
            >
              <Icona nom="chevron-right" />
            </button>
          </div>
        </div>
        <Permis recurs="cai.calendaris" tipus="ALTA">
          <Link className="btn btn--s" to={`/planificacio/calendaris/${id}/rotacio`}>
            <Icona nom="sliders" />
            {t('editor.rotacio')}
          </Link>
        </Permis>
        {potEditar ? (
          <button
            type="button"
            className="btn btn--blue btn--s"
            disabled={!canvis.length || desant}
            onClick={() => void desa()}
          >
            <Icona nom="checkmark" />
            {t('editor.desa', { count: canvis.length })}
          </button>
        ) : null}
      </Toolbar>
      <div className="content">
        {errorDesa ? (
          <div className="avis-banda" role="alert" style={{ marginBottom: 16 }}>
            <Icona nom="exclamationmark-triangle" />
            <span style={{ flex: 1 }}>
              {esConflicteVersio(errorDesa) ? missatgeConflicte : missatgeError(errorDesa)}
            </span>
            {esConflicteVersio(errorDesa) ? (
              <button type="button" className="btn btn--xs" onClick={recarrega}>
                <Icona nom="arrow-clockwise" />
                {t('editor.recarrega')}
              </button>
            ) : null}
          </div>
        ) : null}
        <DndContext sensors={sensors} onDragEnd={enDeixar}>
          <div className="editor">
            <div className="panel">
              <div className="panel__head">
                <h3>{t('editor.graella')}</h3>
                <span className="dim t-footnote">{ambit}</span>
                <div className="spacer" />
                <div className="legend">
                  <span>
                    <i className="sg-block sg-block--titular sg-mostra" />
                    {t('llegenda.titular')}
                  </span>
                  <span>
                    <i className="sg-block sg-block--subst sg-mostra" />
                    {t('llegenda.substitut')}
                  </span>
                  <span>
                    <i className="sg-block sg-block--crisi sg-mostra" />
                    {t('llegenda.crisi')}
                  </span>
                  <span>
                    <i className="sg-block sg-block--buit sg-mostra" />
                    {t('llegenda.senseCobertura')}
                  </span>
                </div>
              </div>
              <div className="graella-scroll">
                <div className="shift-grid" role="grid" aria-label={t('editor.graella')}>
                  <div className="sg-head" role="columnheader">
                    {t('editor.persona')}
                  </div>
                  {dies.map((d) => (
                    <div
                      key={diaClau(d)}
                      role="columnheader"
                      className={`sg-head${diaSetmana(d) >= 5 ? ' is-cap' : ''}${
                        diaClau(d) === avuiClau ? ' is-avui' : ''
                      }`}
                    >
                      {d.getDate()} {t(`diesCurts.${diaSetmana(d)}`)}
                    </div>
                  ))}

                  <div className="sg-name sg-name--cob">
                    <Icona nom="shield-check" />
                    {t('editor.tornDia')}
                  </div>
                  {dies.map((d) => {
                    const torn = actuals.find((x) => cobreixDia(x, d));
                    const nom = torn ? nomPersona(torn.titularId) : '';
                    return (
                      <Cel key={diaClau(d)} dia={d} fila="cob" cobertura>
                        <button
                          type="button"
                          className={`sg-block sg-boto ${torn ? blocTorn(torn, 'T') : 'sg-block--buit'}`}
                          data-dia={diaClau(d)}
                          onClick={() => obreDia(d)}
                          title={torn ? nom : t('editor.senseCobertura')}
                          aria-label={
                            torn
                              ? `${f.data(d)} · ${nom}`
                              : `${f.data(d)} · ${t('editor.senseCobertura')}`
                          }
                        />
                      </Cel>
                    );
                  })}

                  {files.map((pid) => (
                    <FilaPersona
                      key={pid}
                      pid={pid}
                      nom={nomPersona(pid)}
                      dies={dies}
                      actuals={actuals}
                      bloc={blocTorn}
                      onObre={obreDia}
                    />
                  ))}
                </div>
              </div>
              {torns.isError ? <ErrorCarrega onReintenta={() => void torns.refetch()} /> : null}
              {!torns.isLoading && files.length === 0 ? (
                <div className="empty">
                  <Icona nom="calendar" />
                  {t('editor.senseTorns')}
                </div>
              ) : null}
            </div>
            <aside className="panel">
              <div className="panel__head">
                <h3>{t('editor.disponibles')}</h3>
              </div>
              <div className="panel__body stack stack--s" style={{ paddingTop: 10 }}>
                <label className="search" style={{ margin: 0 }}>
                  <Icona nom="magnifier" />
                  <input
                    type="search"
                    placeholder={t('editor.cercaPersona')}
                    aria-label={t('editor.cercaPersona')}
                    value={cerca}
                    onChange={(e) => setCerca(e.target.value)}
                  />
                </label>
                {(cercaUsuaris.data ?? [])
                  .filter((u) => !disponibles.some((p) => p.id === u.id))
                  .map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className="btn btn--ghost btn--xs"
                      style={{ justifyContent: 'flex-start' }}
                      onClick={() => {
                        setExtres([...extres, { id: u.id, nom: nomUsuari(u) }]);
                        setCerca('');
                      }}
                    >
                      <Icona nom="plus" />
                      {t('editor.afegeix', { nom: nomUsuari(u) })}
                    </button>
                  ))}
                <div className="persones">
                  {[...disponibles]
                    .sort((a, b) => (tornsDe(a.id) ? 1 : 0) - (tornsDe(b.id) ? 1 : 0))
                    .map((p) => {
                      const n = tornsDe(p.id);
                      return (
                        <PersonaArrossegable
                          key={p.id}
                          persona={p}
                          detall={
                            n
                              ? t('editor.ambTorns', { count: n })
                              : t('editor.senseTorn', { area: area?.nom ?? ambit })
                          }
                          seleccionada={seleccionada === p.id}
                          onSelecciona={() => setSeleccionada(seleccionada === p.id ? null : p.id)}
                        />
                      );
                    })}
                </div>
                <p className="t-caption dim" style={{ margin: 0 }}>
                  {t('editor.ajuda')}
                </p>
              </div>
            </aside>
          </div>
        </DndContext>
      </div>
      {editant ? (
        <W12ModalTorn
          torn={editant.torn}
          calendari={calendari.data?.nom ?? ''}
          ambit={ambit}
          persones={disponibles}
          errorElimina={eliminaTorn.isError ? missatgeError(eliminaTorn.error) : null}
          eliminant={eliminaTorn.isPending}
          onTanca={() => {
            eliminaTorn.reset();
            setEditant(null);
          }}
          onDesa={(nou) => {
            const copia = [...actuals];
            if (editant.index >= 0) copia[editant.index] = nou;
            else copia.push(nou);
            setEsborrany(copia);
            setEditant(null);
          }}
          onElimina={
            potEditar
              ? () => {
                  const tornId = editant.torn.id;
                  const treu = () => {
                    setEsborrany(
                      esborrany ? esborrany.filter((_x, i) => i !== editant.index) : null,
                    );
                    setEditant(null);
                  };
                  if (tornId != null) eliminaTorn.mutate(tornId, { onSuccess: treu });
                  else treu();
                }
              : undefined
          }
        />
      ) : null}
    </section>
  );
}

function FilaPersona({
  pid,
  nom,
  dies,
  actuals,
  bloc,
  onObre,
}: {
  pid: number;
  nom: string;
  dies: Date[];
  actuals: TornDto[];
  bloc: (t: TornDto, rol: 'T' | 'S') => string;
  onObre: (d: Date) => void;
}) {
  const { t } = useTranslation('planificacio');
  return (
    <>
      <div className="sg-name" data-fila={pid} title={nom}>
        <Avatar nom={nom} />
        <span className="truncate">{nom}</span>
      </div>
      {dies.map((d) => {
        const torn = actuals.find((x) => cobreixDia(x, d));
        const rol = torn?.titularId === pid ? 'T' : torn?.substitutId === pid ? 'S' : null;
        return (
          <Cel key={diaClau(d)} dia={d} fila={String(pid)}>
            {torn && rol ? (
              <button
                type="button"
                className={`sg-block sg-boto ${bloc(torn, rol)}`}
                data-rol={rol}
                aria-label={`${nom} · ${rol === 'T' ? t('llegenda.titular') : t('llegenda.substitut')}`}
                onClick={() => onObre(d)}
              />
            ) : null}
          </Cel>
        );
      })}
    </>
  );
}
